import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { useMemo, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import {
  MissionFilters,
  computeMissionRange,
  type MissionFiltersValue,
} from '@/components/missions/mission-filters'
import type {
  MissionListRow,
  MissionStatusFilter,
} from '@/components/missions/mission-types'
import { MissionsTable } from '@/components/missions/missions-table'
import type {
  ClientLite,
  DriverLite,
} from '@/components/planning/planning-types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { type ApiClient, type PaginatedResponse } from '@/lib/api'
import {
  formatBonusMinutes,
  formatDate,
  formatHours,
} from '@/lib/format'

/**
 * Missions admin listing.
 *
 * Pulls `GET /api/missions` with the filter values and renders
 * a paginated table. Filters live in local state (we'll lift to
 * URL search params with `validateSearch` once admins start
 * sharing links). Status filter maps to the API's
 * `verified` / `readyToSend` query params:
 *
 *   `notVerified` → `?verified=false`
 *   `verified`    → `?verified=true`
 *   `ready`       → `?readyToSend=true`
 *   `all`         → no filter
 *
 * Pagination is client-driven (page number sent to the API),
 * 20 rows per page. The table component handles the optimistic
 * verify / ready toggles; this page worries only about fetching
 * and chrome.
 *
 * CSV export
 * ----------
 * Same filters but `limit=9999` so the admin gets the whole
 * filtered set, regardless of which page they're on. We build
 * the CSV in-browser (no backend route) — the data is small
 * enough (a busy month is ≈ 1 000 missions) and shipping a
 * dedicated `/export` endpoint would add server work for no
 * real gain.
 */

const PAGE_SIZE = 20
const QUERY_NS = 'missions' as const

export function MissionsPage() {
  const api = useApi()

  const [filters, setFilters] = useState<MissionFiltersValue>(() => {
    const range = computeMissionRange('last7')
    return {
      preset: 'last7',
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      driverId: null,
      clientId: null,
      status: 'all',
      search: '',
    }
  })
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)

  // Reset to page 1 every time the filters change. We do it via
  // a derived effect rather than baking it into setFilters so
  // the `setFilters` API stays simple.
  const queryKey = useMemo(
    () =>
      [
        QUERY_NS,
        'list',
        filters.dateFrom,
        filters.dateTo,
        filters.driverId ?? '',
        filters.clientId ?? '',
        filters.status,
        filters.search,
        page,
      ] as const,
    [filters, page],
  )

  function handleFiltersChange(next: MissionFiltersValue): void {
    setFilters(next)
    setPage(1)
  }

  /* -------- list query -------- */

  const missionsQuery = useQuery({
    queryKey,
    queryFn: () => fetchMissions(api, filters, page, PAGE_SIZE),
    placeholderData: (prev: PaginatedResponse<MissionListRow> | undefined) => prev,
    staleTime: 30 * 1000,
  })

  /* -------- reference data for filters -------- */

  const driversQuery = useQuery({
    queryKey: ['users', 'drivers'] as const,
    queryFn: () =>
      api.get<DriverLite[]>('/api/users', { role: 'driver', active: true, limit: 100 }),
    staleTime: 5 * 60 * 1000,
  })
  const clientsQuery = useQuery({
    queryKey: ['clients', 'lite'] as const,
    queryFn: () =>
      api.get<ClientLite[]>('/api/clients', {
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const rows = missionsQuery.data?.data ?? []
  const total = missionsQuery.data?.meta.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0

  /* -------- CSV export -------- */

  async function handleExport(): Promise<void> {
    setExporting(true)
    try {
      // Reuse the same filter shape but with a fat limit so we
      // get everything in one shot.
      const result = await fetchMissions(api, filters, 1, 9999)
      const csv = buildCsv(result.data, clientsQuery.data ?? [])
      downloadCsv(csv, `missions_${filters.dateFrom}_${filters.dateTo}.csv`)
    } finally {
      setExporting(false)
    }
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Missions</h1>
          <p className="text-sm text-text-muted">
            Vérifiez les bons remplis par vos chauffeurs.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            void handleExport()
          }}
          disabled={exporting || rows.length === 0}
        >
          {exporting ? (
            <Spinner label="Export en cours" />
          ) : (
            <>
              <Download size={16} />
              Exporter CSV
            </>
          )}
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent>
          <MissionFilters
            value={filters}
            onChange={handleFiltersChange}
            drivers={driversQuery.data ?? []}
            driversLoading={driversQuery.isLoading}
            clients={clientsQuery.data ?? []}
            clientsLoading={clientsQuery.isLoading}
          />
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {missionsQuery.isLoading ? (
          <div className="py-16 flex justify-center">
            <Spinner size={28} label="Chargement des missions" />
          </div>
        ) : missionsQuery.isError ? (
          <div className="px-6 py-12 text-center text-danger">
            Impossible de charger les missions. Vérifiez votre connexion.
          </div>
        ) : (
          <MissionsTable
            rows={rows}
            clients={clientsQuery.data ?? []}
            queryKey={queryKey as readonly unknown[]}
          />
        )}
      </Card>

      {/* Pagination footer — only when there's something to page through. */}
      {total > 0 ? (
        <div className="mt-4 flex items-center justify-between text-sm text-text-muted">
          <p>
            Affichage {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || missionsQuery.isFetching}
            >
              <ChevronLeft size={14} />
              Précédent
            </Button>
            <span className="px-2 text-text">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || missionsQuery.isFetching}
            >
              Suivant
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      ) : null}
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Fetcher                                                                    */
/* -------------------------------------------------------------------------- */

async function fetchMissions(
  api: ApiClient,
  filters: MissionFiltersValue,
  page: number,
  limit: number,
): Promise<PaginatedResponse<MissionListRow>> {
  const params: Record<string, string | number | boolean> = {
    page,
    limit,
    sortBy: 'date',
    sortOrder: 'desc',
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  }
  if (filters.driverId !== null) params.driverId = filters.driverId
  if (filters.clientId !== null) params.clientId = filters.clientId
  if (filters.search.trim() !== '') params.search = filters.search.trim()
  applyStatusFilter(params, filters.status)
  return api.getPaginated<MissionListRow>('/api/missions', params)
}

function applyStatusFilter(
  params: Record<string, string | number | boolean>,
  status: MissionStatusFilter,
): void {
  switch (status) {
    case 'verified':
      params.verified = true
      break
    case 'notVerified':
      params.verified = false
      break
    case 'ready':
      params.readyToSend = true
      break
    case 'all':
    default:
      // no-op
      break
  }
}

/* -------------------------------------------------------------------------- */
/*  CSV building + download                                                    */
/* -------------------------------------------------------------------------- */

const CSV_HEADERS = [
  'Date',
  'Chantier',
  'Chauffeur',
  'Véhicule',
  'Client',
  'Total (h)',
  'Jour (h)',
  'Nuit (h)',
  'Bonus',
  'Vérifié',
  'Prêt',
] as const

function buildCsv(
  rows: readonly MissionListRow[],
  clients: readonly ClientLite[],
): string {
  // Excel-friendly: BOM + CRLF + comma separator + quoted cells.
  // The BOM is what makes Excel pick UTF-8 instead of cp1252,
  // which would otherwise mangle accents on Windows.
  const lines: string[] = []
  lines.push(CSV_HEADERS.map(quote).join(','))
  for (const row of rows) {
    const clientName =
      row.clientName ??
      clients.find((c) => c.id === row.clientId)?.name ??
      ''
    lines.push(
      [
        formatDate(row.date),
        row.site,
        row.driverName ?? '',
        row.vehicleRegistration ?? '',
        clientName,
        formatHours(row.totalHours),
        formatHours(row.dayHours),
        formatHours(row.nightHours),
        formatBonusMinutes(row.bonusMinutes),
        row.verified ? 'Oui' : 'Non',
        row.readyToSend ? 'Oui' : 'Non',
      ]
        .map(quote)
        .join(','),
    )
  }
  return '\uFEFF' + lines.join('\r\n')
}

function quote(field: string): string {
  // Always quote — keeps the parser deterministic. Embedded
  // quotes are doubled per RFC 4180.
  return `"${String(field).replace(/"/g, '""')}"`
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Free the blob immediately — older browsers won't reclaim it
  // until the page reloads otherwise.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
